import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Checkbox, Form, Modal, Select, Space, Tag } from 'antd';
import { CollaboratorPermission, ProjectCollaboratorDto } from 'api';
import { useEffect, useState } from 'react';
import { getAllCollaboratorPermissions } from 'utils/productUtils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormItem from 'components/form/FormItem';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import styles from './inviteModal.module.less';

interface Props {
	visible: boolean;
	setVisible: (visible: boolean) => void;
	onSubmit: (
		invitationEmails: string[],
		permissions?: CollaboratorPermission[],
	) => Promise<void>;
	projectId: number;
	existingCollaborators?: ProjectCollaboratorDto[];
}

interface FormValues {
	invitationEmails: string[];
	permissions: CollaboratorPermission[];
}

export default function InviteModal(props: Props) {
	const { visible, setVisible, existingCollaborators, onSubmit } = props;
	const { t } = useTranslation();

	const emailSchema = yup
		.string()
		// eslint-disable-next-line no-template-curly-in-string
		.email('${value} must be a valid email')
		.max(
			255,
			t('validation.maxLength', {
				fieldName: t('collaboratorsPage.InviteCollaboratorModal.Email'),
				count: 255,
			}),
		)
		.test(
			'emailIsUnique',
			({ value }) =>
				t('collaboratorsPage.InviteCollaboratorModal.Email.IsAlreadyAdded', {
					email: value,
				}),
			(value) => {
				return !existingCollaborators?.some(
					(collaborator) => collaborator.userEmail === value,
				);
			},
		);

	const formSchema = yup.object().shape({
		invitationEmails: yup
			.array()
			.of(emailSchema)
			.min(
				1,
				t('validation.min', {
					fieldName: t('collaboratorsPage.InviteCollaboratorModal.Email'),
					min: 1,
				}),
			),
		permissions: yup
			.array()
			.of(yup.number())
			.min(
				1,
				t('validation.required', {
					fieldName: t(
						'collaboratorsPage.InviteCollaboratorModal.Form.Permissions',
					),
				}),
			),
	});

	const tagSchema = yup.object().shape({
		email: emailSchema,
	});

	const tagValidation = (email: string) => {
		return tagSchema.isValidSync({ email });
	};

	const [confirmInviteModalLoading, setConfirmInviteModalLoading] =
		useState(false);
	const allPermissions = getAllCollaboratorPermissions(t);
	const { control, handleSubmit, watch, formState, reset, setValue, trigger } =
		useForm<FormValues>({
			mode: 'all',
			resolver: yupResolver(formSchema),
			defaultValues: {
				invitationEmails: [],
				permissions: [],
			},
		});
	const permissions = watch('permissions');

	useEffect(() => reset(), [visible]);

	const submitInvitations = async (values: FormValues) => {
		const { invitationEmails, permissions } = values;
		setConfirmInviteModalLoading(true);

		await onSubmit(invitationEmails, permissions);

		setConfirmInviteModalLoading(false);
	};

	const onCheckAllPermissionsChange = (e: CheckboxChangeEvent) => {
		setValue(
			'permissions',
			e.target.checked ? allPermissions.map((x) => x.value) : [],
		);
		trigger('permissions');
	};

	const tagRender = (props: any) => {
		const { label, value, closable, onClose } = props;
		const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
			event.preventDefault();
			event.stopPropagation();
		};
		const color = tagValidation(value) ? undefined : 'red';
		return (
			<Tag
				color={color}
				onMouseDown={onPreventMouseDown}
				closable={closable}
				onClose={onClose}
				style={{
					marginRight: 3,
					maxWidth: '100%',
					overflow: 'hidden',
					whiteSpace: 'break-spaces',
				}}
			>
				{label}
			</Tag>
		);
	};

	return (
		<>
			{visible && (
				<Modal
					title={
						<span>{t('collaboratorsPage.InviteCollaboratorModal.Header')}</span>
					}
					className={styles.inviteModal}
					visible
					centered
					okText={t('collaboratorsPage.InviteCollaboratorModal.OkButton')}
					cancelText={t('common.cancel')}
					onOk={handleSubmit(submitInvitations)}
					onCancel={() => setVisible(false)}
					cancelButtonProps={{
						className: 'color-dark',
					}}
					okButtonProps={{
						disabled: !formState.isValid,
						type: 'primary',
						htmlType: 'submit',
					}}
					confirmLoading={confirmInviteModalLoading}
					maskClosable={false}
					keyboard={false}
				>
					<Form data-testid="form" layout="vertical">
						<FormItem
							name="invitationEmails"
							label={t('collaboratorsPage.InviteCollaboratorModal.Email')}
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									open={false}
									mode="tags"
									style={{ width: '100%' }}
									tokenSeparators={[',', ' ']}
									tagRender={tagRender}
									data-testid="invitationEmails"
									placeholder={t(
										'collaboratorsPage.InviteCollaboratorModal.Email.Placeholder',
									)}
								/>
							)}
						/>
						<FormItem
							label={t('collaboratorsPage.InviteCollaboratorModal.Permissions')}
							name="permissions"
							className={styles.permissionsContainer}
							control={control}
							render={({ field }) => (
								<div ref={field.ref} tabIndex={0}>
									<Space direction="vertical">
										<Checkbox
											checked={permissions.length === allPermissions.length}
											onChange={onCheckAllPermissionsChange}
										>
											{t('Collaborators.All')}
										</Checkbox>
										<Checkbox.Group {...field}>
											<Space direction="vertical">
												{allPermissions.map((option) => (
													<Checkbox key={option.value} value={option.value}>
														{option.name}
													</Checkbox>
												))}
											</Space>
										</Checkbox.Group>
									</Space>
								</div>
							)}
						/>
					</Form>
				</Modal>
			)}
		</>
	);
}
